import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const PublicNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/a-propos', label: 'À propos' },
    { href: '/services', label: 'Services' },
    { href: '/projets', label: 'Projets' },
    { href: '/equipe', label: 'Équipe' },
    { href: '/academie', label: 'aCADemie', branded: true },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-[#1D1D1B]/95 backdrop-blur-md shadow-lg' 
          : 'bg-[#1D1D1B]'
      }`}
      data-testid="public-navbar"
    >
      <div className="container-app">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="/logo-pey.png" 
              alt="PEY Consultants" 
              className="h-10 md:h-12 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative font-medium transition-colors duration-200 ${
                  isActive(link.href) 
                    ? 'text-[#FFD537]' 
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.branded ? (
                  <span>a<span style={{color: '#FFD537', fontWeight: 700}}>CAD</span>emie</span>
                ) : link.label}
                {isActive(link.href) && (
                  <span 
                    className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                    style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/connexion">
              <Button variant="outline-light" data-testid="login-btn">
                Espace client
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="contact-btn">
                Nous joindre
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {isOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden py-4 border-t border-white/10 animate-fadeIn" 
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(link.href)
                      ? 'text-[#1D1D1B] font-semibold'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                  style={isActive(link.href) ? { 
                    background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' 
                  } : {}}
                  onClick={() => setIsOpen(false)}
                >
                  {link.branded ? (
                    <span>a<span style={{color: isActive(link.href) ? '#1D1D1B' : '#FFD537', fontWeight: 700}}>CAD</span>emie</span>
                  ) : link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-4 px-4 border-t border-white/10 mt-3">
                <Link to="/connexion" onClick={() => setIsOpen(false)}>
                  <Button variant="outline-light" className="w-full">
                    Espace client
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button className="w-full">
                    Nous joindre
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
